import { Box, Typography, useTheme } from '@mui/material';
import Loading from 'components/Loading';
import { getFieldName } from 'helpers/helpers';
import { useIsExploreUser } from 'hooks/client';
import { useAPIGetFieldSynopsis } from 'modules/datacatalog/api/useAPIGetFieldSynopsis';
import { useAPIGetTableSynopsis } from 'modules/datacatalog/api/useAPIGetTableSynopsis';
import type {
    CatalogSelection,
    FieldSynopsis,
    TableSynopsis,
} from 'modules/datacatalog/types';
import { useTranslation } from 'react-i18next';
import Flex from 'ui/Flex/Flex';

import FieldDataPreview from './FieldDataPreview';
import TableDataPreview from './TableDataPreview';

type Props = {
    selection: CatalogSelection;
};

const DataPreview = ({ selection }: Props) => {
    const { t } = useTranslation();
    const isExploreUser = useIsExploreUser();
    const theme = useTheme();
    const isTable = selection.kind === 'table';
    const isCSV = selection.domain === 'CSV';

    const { data: tableData, isLoading: isTableLoading } =
        useAPIGetTableSynopsis(
            selection.item.table,
            isTable && isExploreUser && !isCSV,
        );
    const { data: fieldData, isLoading: isFieldLoading } =
        useAPIGetFieldSynopsis(
            selection.item.table,
            getFieldName(selection.item.field),
            !isTable && isExploreUser && !isCSV,
        );

    if (!isExploreUser || isCSV) {
        return null;
    }

    const isLoading = isTable ? isTableLoading : isFieldLoading;
    const hasSynopsis = isTable
        ? tableData?.hasSynopsis
        : fieldData?.hasSynopsis;

    const renderContent = () => {
        if (isLoading) return <Loading small />;
        if (!hasSynopsis) {
            return (
                <Typography variant="body2" color="textSecondary">
                    {t('dataCatalog.protectedPreview')}
                </Typography>
            );
        }
        return (
            <Box sx={{ ml: theme.spacing(2) }}>
                {isTable ? (
                    <TableDataPreview
                        data={
                            tableData as Extract<
                                TableSynopsis,
                                { hasSynopsis: true }
                            >
                        }
                    />
                ) : (
                    <FieldDataPreview
                        data={
                            fieldData as Extract<
                                FieldSynopsis,
                                { hasSynopsis: true }
                            >
                        }
                    />
                )}
            </Box>
        );
    };

    return (
        <Flex direction="column" gap={1}>
            <Typography variant="h5" color="textPrimary">
                {t('dataCatalog.dataPreview')}
            </Typography>
            {renderContent()}
        </Flex>
    );
};

export default DataPreview;
